import React, { useState } from 'react';
import type { AttendanceRecord } from '../types/attendance';
import type { AttendanceCorrectionRequest } from '../types/attendanceCorrection';

interface AttendanceCorrectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: AttendanceRecord & { isProxySubmit?: boolean };
  existingRequest?: AttendanceCorrectionRequest;
  onSubmit: (data: {
    request_type: 'update' | 'delete' | 'create';
    reason: string;
    requested_clock_in?: string;
    requested_clock_out?: string;
    target_date?: string;
    target_user_id?: number;
  }) => Promise<void>;
  onDelete?: (id: number) => Promise<void>;
}

const AttendanceCorrectionModal: React.FC<AttendanceCorrectionModalProps> = ({
  isOpen,
  onClose,
  record,
  existingRequest,
  onSubmit,
  onDelete
}) => {
  const isNewRecord = !record?.id; // 新規作成かどうか
  const [requestType, setRequestType] = useState<'update' | 'delete' | 'create'>('update');
  const [reason, setReason] = useState('');
  const [requestedClockIn, setRequestedClockIn] = useState('');
  const [requestedClockOut, setRequestedClockOut] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // モーダルが開かれた時に初期値を設定
  React.useEffect(() => {
    if (isOpen && record) {
      // 既存の申請がある場合はその内容を、ない場合は現在の打刻時刻を表示
      if (existingRequest) {
        setRequestType(existingRequest.request_type);
        setReason(existingRequest.reason);

        if (existingRequest.request_type === 'update' || existingRequest.request_type === 'create') {
          if (existingRequest.requested_clock_in) {
            const clockInDate = new Date(existingRequest.requested_clock_in);
            setRequestedClockIn(formatDateTimeLocal(clockInDate));
          } else {
            setRequestedClockIn('');
          }

          if (existingRequest.requested_clock_out) {
            const clockOutDate = new Date(existingRequest.requested_clock_out);
            setRequestedClockOut(formatDateTimeLocal(clockOutDate));
          } else {
            setRequestedClockOut('');
          }
        } else {
          setRequestedClockIn('');
          setRequestedClockOut('');
        }
      } else {
        // 新規作成の場合はrequest_typeを'create'に設定
        if (isNewRecord) {
          setRequestType('create');
          setReason('');
          // デフォルトの出勤時刻を設定（当日の9:00）
          const today = new Date(record.date);
          today.setHours(9, 0, 0, 0);
          setRequestedClockIn(formatDateTimeLocal(today));
          setRequestedClockOut('');
        } else {
          setRequestType('update');
          setReason('');

          // 既存の打刻時刻をdatetime-local形式に変換
          if (record.clock_in) {
            const clockInDate = new Date(record.clock_in);
            setRequestedClockIn(formatDateTimeLocal(clockInDate));
          } else {
            setRequestedClockIn('');
          }

          if (record.clock_out) {
            const clockOutDate = new Date(record.clock_out);
            setRequestedClockOut(formatDateTimeLocal(clockOutDate));
          } else {
            setRequestedClockOut('');
          }
        }
      }

      setError('');
    }
  }, [isOpen, record, existingRequest, isNewRecord]);

  // Date → datetime-local形式の文字列に変換
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // datetime-local形式 → データベース用の文字列に変換（JST）
  const formatToISO = (dateTimeLocal: string): string => {
    if (!dateTimeLocal) return '';
    // datetime-localの値は「YYYY-MM-DDTHH:mm」形式
    // ブラウザのdatetime-localはローカルタイムゾーン（JST）として解釈される
    // JSTのタイムゾーン情報（+09:00）を明示的に追加
    return dateTimeLocal + ':00+09:00';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // バリデーション
    if (!reason.trim()) {
      setError('理由を入力してください');
      return;
    }

    if (requestType === 'update' || requestType === 'create') {
      if (!requestedClockIn) {
        setError('出勤時刻を入力してください');
        return;
      }

      // 出勤時刻と退勤時刻の妥当性チェック
      if (requestedClockOut) {
        const clockInTime = new Date(requestedClockIn).getTime();
        const clockOutTime = new Date(requestedClockOut).getTime();

        if (clockOutTime <= clockInTime) {
          setError('退勤時刻は出勤時刻より後である必要があります');
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);

      const data: any = {
        request_type: requestType,
        reason: reason.trim(),
      };

      if (requestType === 'update') {
        data.requested_clock_in = formatToISO(requestedClockIn);
        if (requestedClockOut) {
          data.requested_clock_out = formatToISO(requestedClockOut);
        }
      } else if (requestType === 'create') {
        data.target_date = record.date;
        data.requested_clock_in = formatToISO(requestedClockIn);
        if (requestedClockOut) {
          data.requested_clock_out = formatToISO(requestedClockOut);
        }
        // 代理申請の場合は target_user_id を設定
        if (record.isProxySubmit && record.user_id) {
          data.target_user_id = record.user_id;
        }
      }

      await onSubmit(data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || '申請の送信に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) return '---';
    return new Date(timeString).toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: 'clamp(16px, 4vw, 24px)',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 'clamp(12px, 3vw, 20px)', fontSize: 'clamp(16px, 4vw, 20px)' }}>
          {/*
            代理かどうかを最優先で見せる。**誰の申請になるか**が一番重要で、
            新規/修正の別はその次。ここが分かれていないと、他人の記録を
            開いているのに自分の申請だと思ったまま送ってしまう。
          */}
          {record.isProxySubmit
            ? (isNewRecord ? '代理申請（打刻の新規登録）' : '代理申請（打刻の修正）')
            : (isNewRecord ? '打刻新規申請' : '打刻修正申請')}
        </h2>

        {/* 代理申請の場合の注意書き */}
        {record.isProxySubmit && (
          <div style={{
            padding: 'clamp(10px, 2.5vw, 12px)',
            backgroundColor: '#d1ecf1',
            borderRadius: '4px',
            marginBottom: 'clamp(12px, 3vw, 16px)',
            border: '1px solid #bee5eb'
          }}>
            <p style={{ margin: 0, fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#0c5460' }}>
              <strong>対象ユーザー:</strong> {(record as any).user_name || '不明'}
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#0c5460' }}>
              他のユーザーの打刻修正を代理で申請します。
            </p>
          </div>
        )}

        {/* 現在の記録情報 */}
        {!isNewRecord && (
          <div style={{
            padding: 'clamp(12px, 3vw, 16px)',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            marginBottom: 'clamp(15px, 4vw, 20px)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 'clamp(10px, 2.5vw, 12px)', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>現在の記録</h3>
            <div style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#495057' }}>
              <p style={{ margin: '4px 0' }}><strong>日付:</strong> {formatDate(record.date)}</p>
              <p style={{ margin: '4px 0' }}><strong>出勤:</strong> {formatTime(record.clock_in)}</p>
              <p style={{ margin: '4px 0' }}><strong>退勤:</strong> {formatTime(record.clock_out)}</p>
            </div>
          </div>
        )}

        {/* 新規作成の場合の情報 */}
        {isNewRecord && (
          <div style={{
            padding: 'clamp(12px, 3vw, 16px)',
            backgroundColor: '#fff3cd',
            borderRadius: '4px',
            marginBottom: 'clamp(15px, 4vw, 20px)',
            border: '1px solid #ffc107'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 'clamp(10px, 2.5vw, 12px)', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>対象日付</h3>
            <div style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#495057' }}>
              <p style={{ margin: '4px 0' }}><strong>日付:</strong> {formatDate(record.date)}</p>
              <p style={{ margin: '4px 0', color: '#856404' }}>※ この日は打刻記録がありません</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 申請タイプ選択（新規作成の場合は非表示） */}
          {!isNewRecord && (
            <div style={{ marginBottom: 'clamp(15px, 4vw, 20px)' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                申請タイプ <span style={{ color: 'red' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 'clamp(15px, 4vw, 20px)', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  <input
                    type="radio"
                    value="update"
                    checked={requestType === 'update'}
                    onChange={(e) => setRequestType(e.target.value as 'update')}
                    style={{ marginRight: '6px' }}
                  />
                  打刻修正
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  <input
                    type="radio"
                    value="delete"
                    checked={requestType === 'delete'}
                    onChange={(e) => setRequestType(e.target.value as 'delete')}
                    style={{ marginRight: '6px' }}
                  />
                  記録削除
                </label>
              </div>
            </div>
          )}

          {/* 修正内容（update または create の場合のみ） */}
          {(requestType === 'update' || requestType === 'create') && (
            <div style={{ marginBottom: 'clamp(15px, 4vw, 20px)' }}>
              <h3 style={{ marginTop: 0, marginBottom: 'clamp(10px, 2.5vw, 12px)', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>修正後の時刻</h3>

              <div style={{ marginBottom: 'clamp(12px, 3vw, 16px)' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  出勤時刻 <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="datetime-local"
                  value={requestedClockIn}
                  onChange={(e) => setRequestedClockIn(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: 'clamp(12px, 3vw, 16px)' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                  退勤時刻
                </label>
                <input
                  type="datetime-local"
                  value={requestedClockOut}
                  onChange={(e) => setRequestedClockOut(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                />
                <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)' }}>※ 空欄の場合は出勤時刻のみ修正されます</small>
              </div>
            </div>
          )}

          {/* 理由 */}
          <div style={{ marginBottom: 'clamp(15px, 4vw, 20px)' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
              {requestType === 'create' ? '申請理由' : (requestType === 'update' ? '修正理由' : '削除理由')} <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="理由を入力してください"
              rows={4}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              required
            />
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div style={{
              padding: 'clamp(8px, 2vw, 10px)',
              marginBottom: 'clamp(12px, 3vw, 16px)',
              borderRadius: '4px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              border: '1px solid #f5c6cb',
              fontSize: 'clamp(12px, 2.5vw, 14px)'
            }}>
              {error}
            </div>
          )}

          {/* ボタン */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                opacity: isSubmitting ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              キャンセル
            </button>
            {/* 既存の申請がある場合は取下げボタンを表示 */}
            {existingRequest && onDelete && (
              <button
                type="button"
                onClick={async () => {
                  if (window.confirm('この修正申請を取り下げますか？')) {
                    try {
                      setIsSubmitting(true);
                      await onDelete(existingRequest.id);
                      onClose();
                    } catch (err: any) {
                      setError(err.response?.data?.error || '取り下げに失敗しました');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }
                }}
                disabled={isSubmitting}
                style={{
                  padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                  backgroundColor: '#ffc107',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  opacity: isSubmitting ? 0.6 : 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {isSubmitting ? '処理中...' : '取下げ'}
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(16px, 4vw, 20px)',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                opacity: isSubmitting ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {isSubmitting ? '送信中...' : '申請する'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceCorrectionModal;
